import * as THREE from "three";

type SuntraceColorBeat = {
  fogColor: THREE.Color;
  fogNear: number;
  fogFar: number;
  hemisphereSkyColor: THREE.Color;
  hemisphereGroundColor: THREE.Color;
  hemisphereIntensity: number;
  sunColor: THREE.Color;
  sunIntensity: number;
  fillColor: THREE.Color;
  fillIntensity: number;
  backgroundIntensity: number;
  environmentIntensity: number;
  exposure: number;
  postLift: THREE.Vector3;
  postGamma: THREE.Vector3;
  postGain: THREE.Vector3;
};

export type SuntraceColorGrade = SuntraceColorBeat;

type SuntraceRouteGradeDefinition = {
  lift: readonly [number, number, number];
  gamma: readonly [number, number, number];
  gain: readonly [number, number, number];
};

const neutralRouteGrade: SuntraceRouteGradeDefinition = {
  lift: [0, 0, 0],
  gamma: [1, 1, 1],
  gain: [1, 1, 1],
};

const createBeat = (
  fogColor: number,
  fogNear: number,
  fogFar: number,
  hemisphereSkyColor: number,
  hemisphereGroundColor: number,
  hemisphereIntensity: number,
  sunColor: number,
  sunIntensity: number,
  fillColor: number,
  fillIntensity: number,
  backgroundIntensity: number,
  environmentIntensity: number,
  exposure: number,
  routeGrade: SuntraceRouteGradeDefinition = neutralRouteGrade,
): SuntraceColorBeat => ({
  fogColor: new THREE.Color(fogColor),
  fogNear,
  fogFar,
  hemisphereSkyColor: new THREE.Color(hemisphereSkyColor),
  hemisphereGroundColor: new THREE.Color(hemisphereGroundColor),
  hemisphereIntensity,
  sunColor: new THREE.Color(sunColor),
  sunIntensity,
  fillColor: new THREE.Color(fillColor),
  fillIntensity,
  backgroundIntensity,
  environmentIntensity,
  exposure,
  postLift: new THREE.Vector3(...routeGrade.lift),
  postGamma: new THREE.Vector3(...routeGrade.gamma),
  postGain: new THREE.Vector3(...routeGrade.gain),
});

const pearlIgnition = createBeat(
  0x72868c,
  320,
  980,
  0x7898a6,
  0x3b403a,
  0.31,
  0xffc98f,
  2.68,
  0x617f90,
  0.18,
  0.53,
  0.6,
  0.86,
  {
    lift: [0.006, 0.003, -0.002],
    gamma: [0.995, 1, 1.008],
    gain: [1.025, 1.01, 0.995],
  },
);

const stormglassRun = createBeat(
  0x788c91,
  410,
  1_380,
  0x819da8,
  0x3b433e,
  0.32,
  0xffd6ad,
  2.62,
  0x668294,
  0.2,
  0.52,
  0.63,
  0.87,
  {
    lift: [-0.003, 0.001, 0.005],
    gamma: [1.008, 1, 0.992],
    gain: [0.995, 1.01, 1.025],
  },
);

const solarReturn = createBeat(
  0x8d8178,
  335,
  1_140,
  0x748f9b,
  0x4a4036,
  0.3,
  0xffb96d,
  2.72,
  0x5d7889,
  0.18,
  0.55,
  0.58,
  0.85,
  {
    lift: [0.008, 0.003, -0.004],
    gamma: [0.99, 0.998, 1.012],
    gain: [1.035, 1.008, 0.98],
  },
);

const smooth = (value: number): number =>
  value * value * (3 - 2 * value);

const blend = (
  from: SuntraceColorBeat,
  to: SuntraceColorBeat,
  amount: number,
  target: SuntraceColorGrade,
): SuntraceColorGrade => {
  const alpha = smooth(THREE.MathUtils.clamp(amount, 0, 1));
  target.fogColor.lerpColors(from.fogColor, to.fogColor, alpha);
  target.fogNear = THREE.MathUtils.lerp(from.fogNear, to.fogNear, alpha);
  target.fogFar = THREE.MathUtils.lerp(from.fogFar, to.fogFar, alpha);
  target.hemisphereSkyColor.lerpColors(
    from.hemisphereSkyColor,
    to.hemisphereSkyColor,
    alpha,
  );
  target.hemisphereGroundColor.lerpColors(
    from.hemisphereGroundColor,
    to.hemisphereGroundColor,
    alpha,
  );
  target.hemisphereIntensity = THREE.MathUtils.lerp(
    from.hemisphereIntensity,
    to.hemisphereIntensity,
    alpha,
  );
  target.sunColor.lerpColors(from.sunColor, to.sunColor, alpha);
  target.sunIntensity = THREE.MathUtils.lerp(
    from.sunIntensity,
    to.sunIntensity,
    alpha,
  );
  target.fillColor.lerpColors(from.fillColor, to.fillColor, alpha);
  target.fillIntensity = THREE.MathUtils.lerp(
    from.fillIntensity,
    to.fillIntensity,
    alpha,
  );
  target.backgroundIntensity = THREE.MathUtils.lerp(
    from.backgroundIntensity,
    to.backgroundIntensity,
    alpha,
  );
  target.environmentIntensity = THREE.MathUtils.lerp(
    from.environmentIntensity,
    to.environmentIntensity,
    alpha,
  );
  target.exposure = THREE.MathUtils.lerp(from.exposure, to.exposure, alpha);
  target.postLift.lerpVectors(from.postLift, to.postLift, alpha);
  target.postGamma.lerpVectors(from.postGamma, to.postGamma, alpha);
  target.postGain.lerpVectors(from.postGain, to.postGain, alpha);
  return target;
};

export const createSuntraceColorGrade = (): SuntraceColorGrade =>
  createBeat(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);

export const sampleSuntraceColorGrade = (
  progress: number,
  target: SuntraceColorGrade,
): SuntraceColorGrade => {
  const finiteProgress = Number.isFinite(progress) ? progress : 0;
  const wrappedProgress = ((finiteProgress % 1) + 1) % 1;
  if (wrappedProgress < 0.04 || wrappedProgress >= 0.94) {
    const wrappedDistance = wrappedProgress >= 0.94
      ? wrappedProgress - 0.94
      : wrappedProgress + 0.06;
    return blend(solarReturn, pearlIgnition, wrappedDistance / 0.1, target);
  }
  if (wrappedProgress < 0.28) {
    return blend(pearlIgnition, pearlIgnition, 0, target);
  }
  if (wrappedProgress < 0.38) {
    return blend(
      pearlIgnition,
      stormglassRun,
      (wrappedProgress - 0.28) / 0.1,
      target,
    );
  }
  if (wrappedProgress < 0.62) {
    return blend(stormglassRun, stormglassRun, 0, target);
  }
  if (wrappedProgress < 0.72) {
    return blend(
      stormglassRun,
      solarReturn,
      (wrappedProgress - 0.62) / 0.1,
      target,
    );
  }
  return blend(solarReturn, solarReturn, 0, target);
};
